import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export const PINNED_ITEMS_FEATURE_KEY = 'pinnedItems';

export interface StoredPinnedItem {
  id: string;
  name: string;
  type: string; // FolderManagerItemTypes or FolderManagerMimeTypes value
  folderId?: number;
  selectId?: string;
  isTrashed?: boolean;
}

interface PinnedItemsState {
  data: Record<string, StoredPinnedItem[]>;
}

const initialState: PinnedItemsState = {
  data: {},
};

const normalizeUserId = (userId: string): string => userId.includes('|') ? userId.split('|')[1] : userId;

export const EMPTY_PINNED_ITEMS: StoredPinnedItem[] = [];

export const pinnedItemsSlice = createSlice({
  name: PINNED_ITEMS_FEATURE_KEY,
  initialState,
  reducers: {
    pinItem: (state, action: PayloadAction<{ userId: string; item: StoredPinnedItem }>) => {
      const { item } = action.payload;
      const key = normalizeUserId(action.payload.userId);
      if (!state.data[key]) {
        state.data[key] = [];
      }
      const exists = state.data[key].some((pinned) => String(pinned.id) === String(item.id));
      if (!exists) {
        state.data[key].unshift(item);
      }
    },
    unpinItem: (state, action: PayloadAction<{ userId: string; itemId: string }>) => {
      const { itemId } = action.payload;
      const key = normalizeUserId(action.payload.userId);
      if (state.data[key]) {
        state.data[key] = state.data[key].filter((item) => String(item.id) !== String(itemId));
      }
    },
  },
});

export const { pinItem, unpinItem } = pinnedItemsSlice.actions;

export const pinnedItemsReducer = pinnedItemsSlice.reducer;

const getPinnedItemsState = (rootState: any): PinnedItemsState => rootState[PINNED_ITEMS_FEATURE_KEY];

export const pinnedItemsSelectors = {
  forUser: (state: any, userId: string): StoredPinnedItem[] => {
    const key = normalizeUserId(userId);
    return getPinnedItemsState(state)?.data?.[key] ?? EMPTY_PINNED_ITEMS;
  },
  isItemPinned: (state: any, userId: string, itemId: string): boolean => {
    const key = normalizeUserId(userId);
    return (getPinnedItemsState(state)?.data?.[key] ?? []).some(
      (item: StoredPinnedItem) => String(item.id) === String(itemId),
    );
  },
};
